import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, faEye, faEyeSlash, faSpinner,
  faUser, faHome, faBell, faCalendarDays
} from '@fortawesome/free-solid-svg-icons';
import { loginUser } from '../utils/auth';
import { authService } from '../services/authService';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      setError('Please enter both email and password');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Use the auth service to authenticate the user
      const result = await authService.login(formData.email, formData.password);
      
      if (result.success && result.user) {
        // Log the user in using the auth utility
        loginUser(result.user);
        
        // Store full user object for authService compatibility
        localStorage.setItem('gomtb_user', JSON.stringify(result.user));
        
        // Navigate to dashboard
        navigate('/dashboard');
      } else {
        setError(result.error || 'Login failed. Please check your credentials.');
      }
      
    } catch (error) {
      console.error('Login error:', error);
      setError('Login failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };





  return (
    <div className="bg-gray-100 font-sans text-dark min-h-screen">
      {/* Mobile Header */}
      <header className="app-header">
        <div className="flex items-center justify-between">
          <Link to="/" className="app-header-back">
            <FontAwesomeIcon icon={faArrowLeft} className="text-lg" />
          </Link>
          <h1 className="app-header-title">Sign In</h1>
          <div className="w-6"></div>
        </div>
      </header>

      {/* Main Content */}
      <main className="content-container pt-8 pb-28">
        <div className="max-w-md mx-auto">
          {/* Welcome Message */}
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome Back</h2>
            <p className="text-gray-600">Sign in to your GOMTB account</p>
          </div>





          {/* Login Form */}
          <form onSubmit={handleLogin} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                placeholder="Enter your email"
                required
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary pr-12"
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-primary text-white font-medium py-3 rounded-lg shadow-md hover:bg-orange-600 transition duration-300 flex items-center justify-center disabled:opacity-50"
            >
              {isSubmitting ? (
                <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          {/* Register Link */}
          <div className="mt-6 text-center">
            <p className="text-gray-600">
              Don't have an account?{' '}
              <Link to="/register" className="text-primary font-medium hover:underline">
                Register here
              </Link>
            </p>
          </div>


        </div>
      </main>

      {/* Bottom Navigation */}
      <nav className="app-bottom-nav">
        <div className="flex justify-around">
          <Link to="/" className="app-bottom-nav-link py-2 px-3">
            <FontAwesomeIcon icon={faHome} className="text-lg" />
            <span className="text-xs mt-1">Home</span>
          </Link>
          <Link to="/events" className="app-bottom-nav-link py-2 px-3">
            <FontAwesomeIcon icon={faCalendarDays} className="text-lg" />
            <span className="text-xs mt-1">Events</span>
          </Link>
          <Link to="/login" className="app-bottom-nav-link-active py-2 px-3">
            <FontAwesomeIcon icon={faUser} className="text-lg" />
            <span className="text-xs mt-1">Sign In</span>
          </Link>
          <span className="app-bottom-nav-link py-2 px-3 cursor-pointer">
            <FontAwesomeIcon icon={faBell} className="text-lg" />
            <span className="text-xs mt-1">Alerts</span>
          </span>
        </div>
      </nav>
    </div>
  );
};

export default Login; 